import React, { useState, useEffect } from "react";
import moment from "moment";
// Redux
import { useSelector } from "react-redux";
// Constants
import { pathHomePage, WEB_SERVICE_DASHBOARD_ROUTE } from "../../config/constants";
// Components
import { Title, CreateDiv, CreateLink, TableHeader, TableTitle, PatientContainer, PatientField } from "./styles";
import Header from "../header";
import Loader from "../loader";
import UseFetch from "../../config/useFetch";

const CodesManagement = () => {
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [patients, setPatients] = useState([]);
  const [currentFilter, setCurrentFilter] = useState("patientUser");

  const isLoggedIn = useSelector(({ user }) => user.isLoggedIn);
  const environment = useSelector(({ database }) => database.environment);

  // Fetching patients
  useEffect(() => {
    setIsLoadingPatients(true);
    const getPatientsSignal = new AbortController();

    if (isLoggedIn && environment) {
      UseFetch("get", `${WEB_SERVICE_DASHBOARD_ROUTE}/patient/get-patients-with-expiration-subscription/${environment}`, getPatientsSignal)
        .then(({ data }) => {
          setPatients(data);
          setIsLoadingPatients(false);
        })
        .catch((error) => {
          console.log("Get patients: ", error);
        });
    }

    return () => {
      setIsLoadingPatients(true);
      setPatients([]);
      if (getPatientsSignal) {
        getPatientsSignal.abort();
      }
    };
  }, [isLoggedIn, environment]);

  const reversePatients = () => {
    setPatients([...patients].reverse());
  };

  const sortByPatient = (e) => {
    const filter = e.target.id;
    if (currentFilter === filter) return reversePatients();

    setCurrentFilter(filter);

    setPatients(
      patients.sort((a, b) => {
        return a.PATIENT_USER.toLowerCase() < b.PATIENT_USER.toLowerCase() ? -1 : 1;
      }),
    );
  };

  const sortByHospital = (e) => {
    const filter = e.target.id;
    if (currentFilter === filter) return reversePatients();

    setCurrentFilter(filter);

    setPatients(
      patients.sort((a, b) => {
        return a.HOSPITAL_NAME.toLowerCase() < b.HOSPITAL_NAME.toLowerCase() ? -1 : 1;
      }),
    );
  };

  const sortByCreation = (e) => {
    const filter = e.target.id;
    if (currentFilter === filter) return reversePatients();

    setCurrentFilter(filter);

    setPatients(
      patients.sort((a, b) => {
        return new Date(a.CREATION_TIME) > new Date(b.CREATION_TIME) ? -1 : 0;
      }),
    );
  };

  const sortByExpirationSubscription = (e) => {
    const filter = e.target.id;
    if (currentFilter === filter) return reversePatients();

    setCurrentFilter(filter);

    setPatients(
      patients.sort((a, b) => {
        if (!a.EXPIRATION_DATE && !b.EXPIRATION_DATE) return 0;
        if (!a.EXPIRATION_DATE) return 1;
        if (!b.EXPIRATION_DATE) return -1;
        return new Date(a.EXPIRATION_DATE) - new Date(b.EXPIRATION_DATE);
      }),
    );
  };

  const sortByCodeUsage = (e) => {
    const filter = e.target.id;
    if (currentFilter === filter) return reversePatients();

    setCurrentFilter(filter);

    setPatients(
      patients.sort((a, b) => {
        return a.USED_A_CODE ? -1 : 0;
      }),
    );
  };

  const renderPatientsHeader = () => {
    return (
      <TableHeader>
        <TableTitle id="patientUser" onClick={sortByPatient} isMarked={currentFilter === "patientUser"} width="240px">
          Patient
        </TableTitle>
        <TableTitle id="hospital" onClick={sortByHospital} isMarked={currentFilter === "hospital"} width="240px">
          Hospital
        </TableTitle>
        <TableTitle id="patientCreation" onClick={sortByCreation} isMarked={currentFilter === "patientCreation"} width="140px">
          Creation date
        </TableTitle>
        <TableTitle id="expiration" onClick={sortByExpirationSubscription} isMarked={currentFilter === "expiration"} width="200px">
          Expiration subscription
        </TableTitle>
        <TableTitle id="usedACode" onClick={sortByCodeUsage} isMarked={currentFilter === "usedACode"} width="140px">
          Used a code
        </TableTitle>
      </TableHeader>
    );
  };

  const renderPatients = () => {
    return patients.map((patient) => {
      const patientId = patient.PATIENT_ID;

      const patientCreation = moment(patient.CREATION_TIME, "YYYY-MM-DD").isValid()
        ? moment(patient.CREATION_TIME, "YYYY-MM-DD").format("DD-MM-YYYY")
        : "N/A";

      const expirationSubscription = moment(patient.EXPIRATION_DATE, "YYYY-MM-DD").isValid()
        ? moment(patient.EXPIRATION_DATE, "YYYY-MM-DD").format("DD-MM-YYYY")
        : "N/A";

      return (
        <PatientContainer key={patientId}>
          <PatientField width="240px">{patient.PATIENT_USER}</PatientField>
          <PatientField width="240px">{patient.HOSPITAL_NAME}</PatientField>
          <PatientField width="140px">{patientCreation}</PatientField>
          <PatientField width="200px">{expirationSubscription}</PatientField>
          <PatientField width="140px">{Boolean(patient.USED_A_CODE) ? "Yes" : "No"}</PatientField>
        </PatientContainer>
      );
    });
  };

  return (
    <>
      <Header />
      <Title>Manage codes</Title>
      <CreateDiv>
        <CreateLink to={`${pathHomePage}/create-code`}>Create new code</CreateLink>
      </CreateDiv>
      {isLoadingPatients ? (
        <Loader />
      ) : (
        <>
          {!patients.length ? (
            <Title>There are no patients</Title>
          ) : (
            <>
              {renderPatientsHeader()}
              {renderPatients()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default CodesManagement;
